import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, NgModule, OnDestroy } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ImgFallbackModule } from '@telespot/shared/util';
import { PaginatedStateLoadingMode, SearchBarComponentModule } from '@shared/ui';
import { map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { organizationStoreProviders, OrganizationData, OrganizationStore } from '@telespot/management/ui';
import { NavBarOrganizationFacade } from './nav-bar-organization.facade';

@Component({
    selector: 'ts-nav-bar-organization',
    templateUrl: './nav-bar-organization.component.html',
    styleUrls: ['./nav-bar-organization.component.scss'],
    providers: [
        NavBarOrganizationFacade,
        OrganizationStore,
        ...organizationStoreProviders(PaginatedStateLoadingMode.CONTINUOUS),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NavBarOrganizationComponent implements OnDestroy {
  public readonly mobile$ = this.breakpoint
    .observe([Breakpoints.HandsetPortrait, Breakpoints.XSmall])
    .pipe(map((v) => v.matches));
  public readonly pageSizeOptions = [10];

  private destroy$ = new Subject<void>();
  public visible$ = this.facade.visible$;
  public hideArrows$ = this.facade.hideArrows$;
  public loadingLogo$ = this.facade.loadingLogo$;

  constructor(
    private router: Router,
    private breakpoint: BreakpointObserver,
    public readonly facade: NavBarOrganizationFacade
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public selectOrganization(organizationId: string): void {
    this.facade.selectOrganization(organizationId);
    this.router.navigate(['/workspaces']);
  }

  public searchTermChanged(value: string): void {
    this.facade.searchTermChanged(value);
  }

  public loadMoreOrganizations(hasMoreItems: boolean): void {
    if (!hasMoreItems) return;
    this.facade.loadMoreOrganizations();
  }

  public onOrganizationListScroll(event: Event, hasMoreItems: boolean, loading: boolean): void {
    if (loading || !hasMoreItems) return;

    const element = event.target as HTMLElement;
    const scrollThreshold = 5; // pixels from bottom to trigger load
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < scrollThreshold;

    if (nearBottom) {
      this.facade.loadMoreOrganizations();
    }
  }

  public trackByOrgId(_index: number, org: OrganizationData): string {
    return org.id;
  }
}

@NgModule({
  declarations: [NavBarOrganizationComponent],
  imports: [
    CommonModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    TranslateModule,
    ImgFallbackModule,
    SearchBarComponentModule,
  ],
  exports: [NavBarOrganizationComponent],
})
export class NavBarOrganizationModule {}
